import React, { Fragment, useMemo } from 'react';
import useIntls from '@opm/hooks/useIntls';
import { TOptionsItem, TSecondOptionsItem } from '../../types';

interface MarketValue {
  type: 'market';
  regions: string[];
  markets: string[];
}

interface CommonSelectButtonProps {
  value?: MarketValue | string[];
  options: any[];
  title: string;
  titleCn: string;
  itemRender?: (option: any) => React.ReactNode;
  // 是否支持扁平选项（默认 false，仅支持二级结构）
  supportFlat?: boolean;
  // 最大显示数量（默认 2，超过则显示数量）
  maxDisplayCount?: number;
}

// 渲染选项项
const renderOptionItem = (item: TSecondOptionsItem, isEn: boolean) => {
  return isEn ? item.text : item.textCN || item.text;
};

export function CommonSelectButton({
  value,
  options,
  title,
  titleCn,
  itemRender,
  supportFlat = false,
  maxDisplayCount = 2,
}: CommonSelectButtonProps) {
  const { intl, isEn } = useIntls();

  // 获取选中的 markets
  const selectedMarkets = useMemo(() => {
    if (Array.isArray(value)) {
      return value;
    }
    return value?.markets || [];
  }, [value]);

  // 检查是否为扁平选项（没有 children）
  const isFlatOptions = useMemo(() => {
    if (!supportFlat) return false;
    return (
      options.length > 0 &&
      !options.some((opt: any) => opt.children && opt.children.length > 0)
    );
  }, [options, supportFlat]);

  // 过滤出所有选中的选项
  const selectOptions = useMemo(() => {
    const selected: TOptionsItem[] = [];

    if (isFlatOptions) {
      // 扁平选项：直接从 options 中过滤
      (options || []).forEach((opt: any) => {
        if (selectedMarkets.includes(opt.value)) {
          selected.push(opt);
        }
      });
    } else {
      // 二级结构：从 children 中过滤
      (options || []).forEach((region: any) => {
        (region.children || []).forEach((child: any) => {
          if (selectedMarkets.includes(child.value)) {
            selected.push(child);
          }
        });
      });
    }

    return selected;
  }, [value, options, selectedMarkets, isFlatOptions]);

  const textList = selectOptions.map((item) => renderOptionItem(item, isEn));
  const markets = Array.from(new Set(selectedMarkets));

  // 计算所有可用的 markets
  const allMarkets = useMemo(() => {
    const result: string[] = [];

    if (isFlatOptions) {
      // 扁平选项：直接收集所有 options 的 value
      (options || []).forEach((opt: any) => {
        result.push(opt.value);
      });
    } else {
      // 二级结构：收集所有 children 的 value
      (options || []).forEach((region: any) => {
        (region.children || []).forEach((child: any) => {
          result.push(child.value);
        });
      });
    }

    return Array.from(new Set(result));
  }, [options, isFlatOptions]);

  // 判断是否全选：选中的 markets 数量等于所有可用的 markets 数量
  const isAll = markets.length === allMarkets.length && markets.length > 0;
  let text: any = textList.length;

  // 使用 itemRender 或默认渲染前 maxDisplayCount 项
  if (itemRender) {
    const rendered = selectOptions.map(itemRender);
    if (rendered.length <= maxDisplayCount) {
      text = (
        <Fragment>
          {rendered.map((item, index) => (
            <Fragment key={index}>
              {item} {rendered.length - 1 === index ? '' : ','}
            </Fragment>
          ))}
        </Fragment>
      );
    }
  } else if (textList.length <= maxDisplayCount) {
    const list = textList.slice(0, maxDisplayCount);
    text = (
      <Fragment>
        {list.map((item, index) => (
          <Fragment key={index}>
            {item} {list.length - 1 === index ? '' : ','}
          </Fragment>
        ))}
      </Fragment>
    );
  }

  // 当 markets 为空时表示全不选
  if (markets.length === 0) {
    text = intl({ id: 'None' });
  }

  if (isAll) {
    text = intl({ id: 'All' });
  }

  return (
    <div className="dbom-dropdow-button">
      <label className="dbom-dropdow-button-label">
        {isEn ? title : titleCn}
      </label>
      <div className="dbom-dropdow-button-value">{text}</div>
    </div>
  );
}
